import { useMemo } from 'react'
import * as THREE from 'three'
import type { ShipTier } from './shipTypes'
import { SHIP_TIERS } from './shipTypes'

// Colors
const HULL_DARK = new THREE.Color('#3F2D20')
const HULL_MID = new THREE.Color('#5C3A1E')
const DECK_COLOR = new THREE.Color('#8B6B4A')
const MAST_COLOR = new THREE.Color('#4A3622')
const SAIL_COLOR = new THREE.Color('#E8DCC8')
const CANNON_COLOR = new THREE.Color('#2B241F')
const RAILING_COLOR = new THREE.Color('#6B5D4F')
const LANTERN_FRAME = new THREE.Color('#4A3622')
const LANTERN_GLASS = new THREE.Color('#FFD080')

interface ShipMeshProps {
  tier: ShipTier
  /** Custom material for GPU buoyancy. If not provided, uses MeshStandardMaterial. */
  material?: THREE.ShaderMaterial
}

/** Hull width multiplier at parametric t (0=stern, 1=bow) */
function hullWidthAt(t: number, bowTaper: number, sternTaper: number): number {
  const peak = 0.4
  if (t < peak) {
    return sternTaper + (1 - sternTaper) * Math.sin((t / peak) * Math.PI * 0.5)
  } else {
    const bt = (t - peak) / (1 - peak)
    return 1 - (1 - bowTaper) * bt * bt
  }
}

function buildShipGeometry(tier: ShipTier): THREE.BufferGeometry {
  const info = SHIP_TIERS[tier]
  const { hullWidth: W, hullLength: L, hullHeight: H, mastCount, cannonsPerSide } = info

  const positions: number[] = []
  const colors: number[] = []

  const halfW = W / 2
  const halfL = L / 2
  const keelDepth = H * 0.6
  const deckY = H * 0.4
  const bowTaper = 0.15
  const sternTaper = 0.65
  const segments = 12 // more segments for smoother hull

  function addTri(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    col: THREE.Color,
  ) {
    positions.push(ax, ay, az, bx, by, bz, cx, cy, cz)
    for (let i = 0; i < 3; i++) colors.push(col.r, col.g, col.b)
  }

  function addQuad(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    dx: number, dy: number, dz: number,
    col: THREE.Color,
  ) {
    addTri(ax, ay, az, bx, by, bz, cx, cy, cz, col)
    addTri(ax, ay, az, cx, cy, cz, dx, dy, dz, col)
  }

  function addBox(
    cx: number, cy: number, cz: number,
    sx: number, sy: number, sz: number,
    col: THREE.Color,
  ) {
    const x0 = cx - sx / 2, x1 = cx + sx / 2
    const y0 = cy - sy / 2, y1 = cy + sy / 2
    const z0 = cz - sz / 2, z1 = cz + sz / 2
    addQuad(x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1, col) // front
    addQuad(x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0, col) // back
    addQuad(x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1, col) // right
    addQuad(x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0, col) // left
    addQuad(x0, y1, z1, x1, y1, z1, x1, y1, z0, x0, y1, z0, col) // top
    addQuad(x0, y0, z0, x1, y0, z0, x1, y0, z1, x0, y0, z1, col) // bottom
  }

  // ── Helper: get hull profile at parametric t ──
  function profileAt(t: number) {
    const z = -halfL + t * L
    const w = halfW * hullWidthAt(t, bowTaper, sternTaper)
    const dw = w * 0.85 // deck width (slightly narrower due to hull flare)
    return { z, w, dw }
  }

  // ── Hull strips ──
  for (let i = 0; i < segments; i++) {
    const t0 = i / segments
    const t1 = (i + 1) / segments
    const p0 = profileAt(t0)
    const p1 = profileAt(t1)

    // Port keel (keel center → port waterline)
    addQuad(
      0, -keelDepth, p0.z,
      -p0.w, 0, p0.z,
      -p1.w, 0, p1.z,
      0, -keelDepth, p1.z,
      HULL_DARK,
    )
    // Starboard keel
    addQuad(
      p0.w, 0, p0.z,
      0, -keelDepth, p0.z,
      0, -keelDepth, p1.z,
      p1.w, 0, p1.z,
      HULL_DARK,
    )

    // Port side (waterline → deck)
    addQuad(
      -p0.w, 0, p0.z,
      -p0.dw, deckY, p0.z,
      -p1.dw, deckY, p1.z,
      -p1.w, 0, p1.z,
      HULL_MID,
    )
    // Starboard side
    addQuad(
      p1.w, 0, p1.z,
      p1.dw, deckY, p1.z,
      p0.dw, deckY, p0.z,
      p0.w, 0, p0.z,
      HULL_MID,
    )

    // Deck
    addQuad(
      -p0.dw, deckY, p0.z,
      p0.dw, deckY, p0.z,
      p1.dw, deckY, p1.z,
      -p1.dw, deckY, p1.z,
      DECK_COLOR,
    )
  }

  // ── Stern cap (t=0, z=-halfL) ──
  {
    const p = profileAt(0)
    // Lower V (keel to waterline, both sides)
    addTri(0, -keelDepth, p.z, -p.w, 0, p.z, p.w, 0, p.z, HULL_DARK)
    // Upper trapezoid (waterline to deck, port side)
    addTri(-p.w, 0, p.z, -p.dw, deckY, p.z, 0, 0, p.z, HULL_MID)
    addTri(0, 0, p.z, -p.dw, deckY, p.z, p.dw, deckY, p.z, HULL_MID)
    // Upper trapezoid starboard
    addTri(0, 0, p.z, p.dw, deckY, p.z, p.w, 0, p.z, HULL_MID)
    // Deck cap at stern
    addTri(-p.dw, deckY, p.z, 0, deckY, p.z, p.dw, deckY, p.z, DECK_COLOR)
  }

  // ── Bow cap (t=1, z=+halfL) ──
  {
    const p = profileAt(1)
    // The bow is very narrow. Close it with triangles converging at the center.
    // Keel to waterline
    addTri(0, -keelDepth, p.z, p.w, 0, p.z, -p.w, 0, p.z, HULL_DARK)
    // Waterline to deck (port)
    addTri(-p.w, 0, p.z, 0, 0, p.z, -p.dw, deckY, p.z, HULL_MID)
    addTri(0, 0, p.z, p.dw, deckY, p.z, -p.dw, deckY, p.z, HULL_MID)
    // Waterline to deck (starboard)
    addTri(p.w, 0, p.z, p.dw, deckY, p.z, 0, 0, p.z, HULL_MID)
    // Deck cap at bow
    addTri(p.dw, deckY, p.z, -p.dw, deckY, p.z, 0, deckY, p.z, DECK_COLOR)
  }

  // ── Stern cabin ──
  const cabinL = L * 0.2
  const cabinH = H * 0.5
  const cabinW = halfW * sternTaper * 0.75
  addBox(0, deckY + cabinH / 2, -halfL + cabinL / 2, cabinW * 2, cabinH, cabinL, HULL_MID)
  addBox(0, deckY + cabinH + 0.15, -halfL + cabinL / 2, cabinW * 2.1, 0.3, cabinL + 0.3, DECK_COLOR)

  // ── Bowsprit ──
  const bowspritLen = L * 0.15
  addBox(0, deckY + 0.3, halfL + bowspritLen / 2, 0.2, 0.2, bowspritLen, MAST_COLOR)

  // ── Masts ──
  const mastHeight = H * 2.5
  const mastRadius = 0.15
  const mastPositions: number[] = []
  if (mastCount === 1) mastPositions.push(0)
  else if (mastCount === 2) mastPositions.push(L * 0.1, -L * 0.15)
  else mastPositions.push(L * 0.2, 0, -L * 0.2)

  for (let mi = 0; mi < mastPositions.length; mi++) {
    const mz = mastPositions[mi]
    const mh = mi === 0 ? mastHeight : mastHeight * 0.85

    // Mast pole
    addBox(0, deckY + mh / 2, mz, mastRadius * 2, mh, mastRadius * 2, MAST_COLOR)

    // Yard (horizontal crossbar)
    const yardW = halfW * 1.6
    const yardY = deckY + mh * 0.7
    addBox(0, yardY, mz, yardW * 2, 0.12, 0.12, MAST_COLOR)

    // Sail (double-sided quad)
    const sailTop = yardY
    const sailBot = deckY + mh * 0.25
    const sailW = yardW * 0.9
    addQuad(
      -sailW, sailBot, mz + 0.3,
      sailW, sailBot, mz + 0.3,
      sailW, sailTop, mz + 0.3,
      -sailW, sailTop, mz + 0.3,
      SAIL_COLOR,
    )
    addQuad(
      sailW, sailBot, mz + 0.25,
      -sailW, sailBot, mz + 0.25,
      -sailW, sailTop, mz + 0.25,
      sailW, sailTop, mz + 0.25,
      SAIL_COLOR,
    )
  }

  // ── Cannons ──
  const cannonSize = 0.4
  const cannonLen = W * 0.4
  const cannonSpacing = (L * 0.5) / Math.max(cannonsPerSide, 1)
  const cannonStartZ = -L * 0.15

  for (let ci = 0; ci < cannonsPerSide; ci++) {
    const cz = cannonStartZ + ci * cannonSpacing
    const t = (cz + halfL) / L
    const dw = halfW * hullWidthAt(t, bowTaper, sternTaper) * 0.85

    addBox(-dw + cannonLen / 2, deckY + cannonSize / 2, cz, cannonLen, cannonSize, cannonSize, CANNON_COLOR)
    addBox(dw - cannonLen / 2, deckY + cannonSize / 2, cz, cannonLen, cannonSize, cannonSize, CANNON_COLOR)
  }

  // ── Railings ──
  const railH = H * 0.25
  for (let i = 0; i < segments; i++) {
    const t0 = i / segments
    const t1 = (i + 1) / segments
    const p0 = profileAt(t0)
    const p1 = profileAt(t1)

    // Port railing (outside face)
    addQuad(
      -p0.dw, deckY, p0.z,
      -p0.dw, deckY + railH, p0.z,
      -p1.dw, deckY + railH, p1.z,
      -p1.dw, deckY, p1.z,
      RAILING_COLOR,
    )
    // Port railing (inside face)
    addQuad(
      -p1.dw, deckY, p1.z,
      -p1.dw, deckY + railH, p1.z,
      -p0.dw, deckY + railH, p0.z,
      -p0.dw, deckY, p0.z,
      RAILING_COLOR,
    )
    // Starboard railing (outside face)
    addQuad(
      p1.dw, deckY, p1.z,
      p1.dw, deckY + railH, p1.z,
      p0.dw, deckY + railH, p0.z,
      p0.dw, deckY, p0.z,
      RAILING_COLOR,
    )
    // Starboard railing (inside face)
    addQuad(
      p0.dw, deckY, p0.z,
      p0.dw, deckY + railH, p0.z,
      p1.dw, deckY + railH, p1.z,
      p1.dw, deckY, p1.z,
      RAILING_COLOR,
    )
    // Railing top cap
    addQuad(
      -p0.dw, deckY + railH, p0.z,
      -p1.dw, deckY + railH, p1.z,
      -p1.dw - 0.05, deckY + railH, p1.z,
      -p0.dw - 0.05, deckY + railH, p0.z,
      RAILING_COLOR,
    )
    addQuad(
      p0.dw, deckY + railH, p0.z,
      p1.dw, deckY + railH, p1.z,
      p1.dw + 0.05, deckY + railH, p1.z,
      p0.dw + 0.05, deckY + railH, p0.z,
      RAILING_COLOR,
    )
  }

  // ── Bow lantern ──
  const lanternZ = halfL - L * 0.03
  const lanternY = deckY
  const postH = H * 0.6
  // Post
  addBox(0, lanternY + postH / 2, lanternZ, 0.12, postH, 0.12, LANTERN_FRAME)
  // Lantern cage (4 vertical bars + top/bottom caps)
  const cageSize = 0.35
  const cageY = lanternY + postH + cageSize / 2 + 0.05
  // Bottom plate
  addBox(0, cageY - cageSize / 2, lanternZ, cageSize + 0.1, 0.06, cageSize + 0.1, LANTERN_FRAME)
  // Top cap (pyramid-ish)
  addBox(0, cageY + cageSize / 2 + 0.03, lanternZ, cageSize + 0.1, 0.06, cageSize + 0.1, LANTERN_FRAME)
  addBox(0, cageY + cageSize / 2 + 0.1, lanternZ, cageSize * 0.5, 0.06, cageSize * 0.5, LANTERN_FRAME)
  // Corner bars
  const barOff = cageSize / 2 - 0.02
  addBox(-barOff, cageY, lanternZ - barOff, 0.05, cageSize, 0.05, LANTERN_FRAME)
  addBox(barOff, cageY, lanternZ - barOff, 0.05, cageSize, 0.05, LANTERN_FRAME)
  addBox(-barOff, cageY, lanternZ + barOff, 0.05, cageSize, 0.05, LANTERN_FRAME)
  addBox(barOff, cageY, lanternZ + barOff, 0.05, cageSize, 0.05, LANTERN_FRAME)
  // Glowing center (slightly smaller, warm glass color)
  addBox(0, cageY, lanternZ, cageSize * 0.6, cageSize * 0.7, cageSize * 0.6, LANTERN_GLASS)

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  geo.computeVertexNormals()
  return geo
}

export { buildShipGeometry }

export function ShipMesh({ tier, material }: ShipMeshProps) {
  const geometry = useMemo(() => buildShipGeometry(tier), [tier])

  return (
    <mesh
      geometry={geometry}
      material={material ?? undefined}
      frustumCulled={!material}
    >
      {!material && (
        <meshStandardMaterial
          vertexColors
          metalness={0.2}
          roughness={0.8}
          side={THREE.DoubleSide}
        />
      )}
    </mesh>
  )
}
